import type { ExpressionSpecification, MapMouseEvent, PaddingOptions } from 'mapbox-gl';
import { type ComponentProps, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { type MapRef, Source } from 'react-map-gl/mapbox';
import type MapLocation from '@alltrails/amplitude/enums/MapLocation';
import logExploreMapPinSelected from '@alltrails/amplitude/events/logExploreMapPinSelected';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import type TrailActivityType from '@alltrails/trails/types/TrailActivityType';
import type { TrailheadInView } from '@alltrails/types/Trailhead';
import useHideBasemapTrailheadLayer from '../../hooks/useHideBasemapTrailheadLayer';
import useMap from '../../hooks/useMap';
import { useAppDispatch, useAppSelector } from '../../redux';
import { type TrailResult, updateActiveTrails, updateCursor } from '../../redux/reducer';
import { setHoveredTrailheadId, setTrailheadsInView } from '../../redux/trailhead';
import { expandedTrailheadIds, trailheadsFromFeatures, trailheadsMinZoom, visibleMapQueryBox } from '../../utils/countTrailheadsInView';
import { featureLayers, trailheadsOverlayCollapsedActiveId, trailheadsOverlayCollapsedId, trailheadsOverlayLayerId } from '../../utils/layers';
import pinZoomPopularityMap from '../../utils/pinZoomPopularityMap';
import { buildTrailheadResultExpressions, trailsByTrailheadId } from '../../utils/trailheadTrailResults';
import { MapLayerInteractionsContext } from '../MapLayerInteractionsProvider';
import TrailheadPopup, { type SelectedTrailhead } from '../TrailheadPopup/TrailheadPopup';
import TrailheadVectorPinLayer from '../TrailheadVectorPinLayer/TrailheadVectorPinLayer';
import ActiveTrailPolylines from '../TrailResults/ActiveTrailPolylines';
import ActiveTrailPopup from '../TrailResults/ActiveTrailPopup';

type TrailheadVectorOverlayProps = {
  activityType?: TrailActivityType; // Used to display the correct trail card stats
  handleFavoriteClick?: (trailId: number) => void; // Used to handle interacting with trail card heart button
  mapLocation: MapLocation | string; // Accept string for legacy PageStrings support
  mapPadding?: PaddingOptions;
} & Required<Pick<ComponentProps<typeof ActiveTrailPopup>, 'cardLocation' | 'detailedCardLocation'>> &
  Pick<
    ComponentProps<typeof ActiveTrailPopup>,
    'inspirationalPageSlug' | 'onTrailCardViewed' | 'onTrailNavigate' | 'getCTAProps' | 'getTrailCardUrl'
  >;

const apiKey = process.env.NEXT_PUBLIC_API_KEY!;
const sourceId = 'trailheads-overlay';
const sourceLayer = 'trailheads';
const pinLayerIds = [trailheadsOverlayLayerId, trailheadsOverlayCollapsedId, trailheadsOverlayCollapsedActiveId];
const inactiveExpression: ExpressionSpecification = ['literal', false];
const collapseEveryPinExpression: ExpressionSpecification = ['literal', true];
const hasTrailsFilter: ExpressionSpecification = ['>', ['coalesce', ['get', 'trail_count'], 0], 0];

// Avoid rendering too many polylines or trail cards at once for now for performance reasons
// There are trailheads on alpha with over 400 trails
const maxPolylines = 10;
const maxPopupTrails = 10;

// A hovered pin swaps between the collapsed and collapsed-active layers, and both Mapbox and the
// interactions provider report that swap as a leave on the layer the pin came from. Reading what is
// actually under the cursor tells the swap apart from the cursor leaving the pin, and keeps a pin
// that another layer now covers from staying hovered.
const isTrailheadUnderCursor = (map: MapRef, point: MapMouseEvent['point']) => {
  const renderedLayerIds = featureLayers.filter(layerId => map.getLayer(layerId));
  if (!renderedLayerIds.length) {
    return false;
  }
  const topLayerId = map.queryRenderedFeatures(point, { layers: renderedLayerIds })[0]?.layer?.id;
  return topLayerId !== undefined && pinLayerIds.includes(topLayerId);
};

const sameTrailheadIds = (current: TrailheadInView['id'][], next: TrailheadInView['id'][]) =>
  current.length === next.length && current.every((id, index) => id === next[index]);

export default function TrailheadVectorOverlay({
  activityType,
  cardLocation,
  detailedCardLocation,
  getCTAProps,
  getTrailCardUrl,
  handleFavoriteClick: handleFavoriteClickProp,
  inspirationalPageSlug,
  mapLocation,
  mapPadding,
  onTrailCardViewed,
  onTrailNavigate
}: TrailheadVectorOverlayProps) {
  const publicBaseUrl = usePublicBaseUrl();
  const map = useMap();
  useHideBasemapTrailheadLayer(map);
  const dispatch = useAppDispatch();
  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);
  const isMobile = useIsMobileSizedScreen();
  const hoveredTrailheadId = useAppSelector(state => state.trailhead.hoveredTrailheadId);
  const { mapZoomBucket, trailResults } = useAppSelector(state => ({
    mapZoomBucket: state.map.mapZoomBucket,
    trailResults: state.map.trailResults
  }));
  const [expandedIds, setExpandedIds] = useState<TrailheadInView['id'][]>([]);
  const [selectedTrailhead, setSelectedTrailhead] = useState<SelectedTrailhead | undefined>();
  // Hover borrows activeTrails to draw polylines, so the popup's trail is kept here to restore on
  // the way out rather than reading state the map listeners would have to re-subscribe to.
  const popupTrailsRef = useRef<{ id: TrailheadInView['id']; trails: TrailResult[] } | undefined>(undefined);
  const trailsByTrailhead = useMemo(() => trailsByTrailheadId(trailResults), [trailResults]);
  // Click and hover read the latest search through this ref. Depending on it directly recreates
  // those handlers on every refresh, and the interaction cleanup then runs while the pointer can
  // still be sitting on a pin.
  const trailsByTrailheadRef = useRef(trailsByTrailhead);
  const tiles = [`${publicBaseUrl}/wmts/overlay/trailheads/{z}/{x}/{y}?key=${apiKey}`];
  const { maxDesktopRegularPins, maxMobileRegularPins } = pinZoomPopularityMap(mapZoomBucket);
  const maxExpandedPins = isMobile ? maxMobileRegularPins : maxDesktopRegularPins;

  const analyticsInfo = useMemo(
    () => (detailedCardLocation && mapLocation ? { detailedCardLocation, page: mapLocation } : undefined),
    [detailedCardLocation, mapLocation]
  );
  const {
    isFavorite: isSavedUtil,
    isComplete: isCompletedUtil,
    isVerified: isVerifiedUtil,
    handleFavoriteClick,
    listModal,
    signUpModal
  } = useReduxListItems(undefined, undefined, analyticsInfo);
  const isTrailSaved = useCallback((trailId: number) => isSavedUtil('trail', trailId), [isSavedUtil]);
  const isTrailCompleted = useCallback((trailId: number) => isCompletedUtil('trail', trailId), [isCompletedUtil]);
  const isTrailVerified = useCallback((trailId: number) => isVerifiedUtil('trail', trailId), [isVerifiedUtil]);
  const handleTrailFavoriteClick = useCallback(
    (trailId: number) => (handleFavoriteClickProp ? handleFavoriteClickProp(trailId) : handleFavoriteClick('trail', trailId)),
    [handleFavoriteClickProp, handleFavoriteClick]
  );

  const isHoveredExpression: ExpressionSpecification = ['==', ['id'], hoveredTrailheadId ?? -1];
  const isSelectedExpression: ExpressionSpecification = ['==', ['id'], selectedTrailhead?.id ?? -1];
  const { isClosedExpression, isCompletedExpression, isSavedExpression, matchedTrailheadsFilter, popularityExpression, trailCountExpression } =
    useMemo(() => buildTrailheadResultExpressions(trailResults, { isTrailCompleted, isTrailSaved }), [isTrailCompleted, isTrailSaved, trailResults]);
  const pinFilter = useMemo<ExpressionSpecification>(() => ['all', hasTrailsFilter, matchedTrailheadsFilter], [matchedTrailheadsFilter]);

  // Mirrors what trailCountExpression shows on the label, so ranking can't send an expanded slot to a
  // trailhead with more total trails but fewer that actually matched the current search. Read through a
  // ref rather than a dependency of the idle-driven effect below, so a new search doesn't tear down and
  // re-subscribe that map listener.
  const matchedTrailCountById = useMemo(() => {
    const counts: Record<string, number> = {};
    trailsByTrailhead.forEach((trails, trailheadId) => {
      counts[String(trailheadId)] = trails.length;
    });
    return counts;
  }, [trailsByTrailhead]);
  const matchedTrailCountByIdRef = useRef(matchedTrailCountById);

  useEffect(() => {
    matchedTrailCountByIdRef.current = matchedTrailCountById;
  }, [matchedTrailCountById]);

  useEffect(() => {
    trailsByTrailheadRef.current = trailsByTrailhead;
  }, [trailsByTrailhead]);

  // The ranking can only read pins the map already drew, so a trailhead that just came into view has
  // to render once before it can win an expanded pin. It renders as a dot until then, so zooming in
  // can't flash a screen of full pins that the next idle pass immediately takes back. Zoomed all the
  // way in there is no cap, so nothing collapses.
  const isCollapsedExpression = useMemo<ExpressionSpecification>(() => {
    if (maxExpandedPins === undefined) {
      return inactiveExpression;
    }
    return expandedIds.length ? ['!', ['in', ['id'], ['literal', expandedIds]]] : collapseEveryPinExpression;
  }, [expandedIds, maxExpandedPins]);
  const selectedTrails = useMemo(
    // TODO Marni: for now, only render 10 trail cards at once for performance reasons
    // Create CTA to expand the trailhead to see all trails while still showing "1 of {total} trails" in carousel controls
    () => {
      const trailheadId = Number(selectedTrailhead?.id);
      if (!selectedTrailhead || !Number.isSafeInteger(trailheadId)) {
        return [];
      }
      return (trailsByTrailhead.get(trailheadId) ?? []).slice(0, maxPopupTrails);
    },
    [selectedTrailhead, trailsByTrailhead]
  );

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const updateTrailheadsInView = () => {
      // Collapsed pins have to stay in the query so a trailhead that collapsed this pass can still
      // win an expanded pin on the next one.
      const renderedLayerIds = pinLayerIds.filter(layerId => map.getLayer(layerId));
      if (!renderedLayerIds.length || !mapPadding) {
        dispatch(setTrailheadsInView([]));
        return;
      }
      const queryBox = visibleMapQueryBox(map.getCanvas(), mapPadding);
      const features = queryBox
        ? map.queryRenderedFeatures(queryBox, { layers: renderedLayerIds })
        : map.queryRenderedFeatures({ layers: renderedLayerIds });
      const trailheads = trailheadsFromFeatures(features);
      dispatch(setTrailheadsInView(trailheads));
      const rankedTrailheads = trailheads.map(trailhead => {
        const matchedTrailCount = matchedTrailCountByIdRef.current[String(trailhead.id)];
        return matchedTrailCount === undefined ? trailhead : { ...trailhead, trailCount: matchedTrailCount };
      });
      const nextExpandedIds = maxExpandedPins === undefined ? [] : expandedTrailheadIds(rankedTrailheads, maxExpandedPins);
      setExpandedIds(currentIds => (sameTrailheadIds(currentIds, nextExpandedIds) ? currentIds : nextExpandedIds));
    };

    updateTrailheadsInView();
    map.on('idle', updateTrailheadsInView);
    return () => {
      map.off('idle', updateTrailheadsInView);
      dispatch(setTrailheadsInView([]));
    };
  }, [dispatch, map, maxExpandedPins, mapPadding]);

  const clearSelection = useCallback(() => {
    popupTrailsRef.current = undefined;
    setSelectedTrailhead(undefined);
    dispatch(updateActiveTrails(undefined));
  }, [dispatch]);

  const openTrailhead = useCallback(
    (event: MapMouseEvent) => {
      const feature = event.features?.[0];
      if (!feature || feature.geometry.type !== 'Point' || feature.id === undefined) {
        return;
      }
      // Marks the click as handled so the map-level listener below can tell a pin apart from the map.
      event.preventDefault();
      const trailheadId = Number(feature.id);
      const trails = Number.isSafeInteger(trailheadId) ? (trailsByTrailheadRef.current.get(trailheadId) ?? []) : [];
      if (!trails.length) {
        clearSelection();
        return;
      }

      const [lng, lat] = feature.geometry.coordinates;
      const coordinates = { lat, lng };
      popupTrailsRef.current = { id: feature.id, trails: [trails[0]] };
      // The selected sprite takes over from the hovered one, and hover stays out while the popup is open.
      dispatch(setHoveredTrailheadId(undefined));
      setSelectedTrailhead({ coordinates, id: feature.id, name: feature.properties?.name });
      dispatch(updateActiveTrails([trails[0]]));
      if (cardLocation) {
        logExploreMapPinSelected({
          action_taken: 'clicked on explore map',
          pin_type: 'trailhead',
          trail_id: trails[0].ID,
          map_location: mapLocation as MapLocation | undefined
        });
      }
    },
    [cardLocation, clearSelection, dispatch, mapLocation]
  );

  // mousemove rather than mouseenter so moving between two adjacent pins still reports the new one.
  const hoverTrailhead = useCallback(
    (event: MapMouseEvent) => {
      dispatch(updateCursor('pointer'));
      // An open popup owns the pin state and the polyline. Other pins only show they can be clicked.
      if (popupTrailsRef.current) {
        return;
      }
      const feature = event.features?.[0];
      dispatch(setHoveredTrailheadId(feature?.id));
      if (!feature) {
        return;
      }
      const trailheadId = Number(feature.id);
      const trails = Number.isSafeInteger(trailheadId) ? (trailsByTrailheadRef.current.get(trailheadId) ?? []) : [];
      dispatch(updateActiveTrails(trails.length ? trails : undefined));
    },
    [dispatch]
  );

  const leaveTrailhead = useCallback(
    (event?: MapMouseEvent) => {
      if (map && event?.point && isTrailheadUnderCursor(map, event.point)) {
        return;
      }
      dispatch(updateCursor(undefined));
      if (popupTrailsRef.current) {
        return;
      }
      dispatch(setHoveredTrailheadId(undefined));
      dispatch(updateActiveTrails(undefined));
    },
    [dispatch, map]
  );

  useEffect(() => {
    pinLayerIds.forEach(layerId => {
      registerEventHandler(layerId, 'click', openTrailhead);
      // Touch fires a synthetic mousemove on tap with no leave to follow, which would strand a
      // tapped pin in the hovered state.
      if (!isMobile) {
        registerEventHandler(layerId, 'mousemove', hoverTrailhead);
        registerEventHandler(layerId, 'mouseleave', leaveTrailhead);
      }
    });
    return () => {
      pinLayerIds.forEach(layerId => {
        unregisterEventHandler(layerId, 'click', openTrailhead);
        unregisterEventHandler(layerId, 'mousemove', hoverTrailhead);
        unregisterEventHandler(layerId, 'mouseleave', leaveTrailhead);
      });
      dispatch(updateCursor(undefined));
      dispatch(setHoveredTrailheadId(undefined));
      // Re-binding the handlers is not a leave, so an open popup keeps its polyline.
      dispatch(updateActiveTrails(popupTrailsRef.current?.trails));
    };
  }, [dispatch, hoverTrailhead, isMobile, leaveTrailhead, openTrailhead, registerEventHandler, unregisterEventHandler]);

  // Drops the popup polyline on unmount whichever order React runs this and the cleanup above:
  // an emptied ref makes that restore a clear, and a later run clears what it restored.
  useEffect(() => {
    return () => {
      popupTrailsRef.current = undefined;
      dispatch(updateActiveTrails(undefined));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const closeOnMapClick = (event: MapMouseEvent) => {
      if (!event.defaultPrevented) {
        clearSelection();
      }
    };

    map.on('click', closeOnMapClick);
    return () => {
      map.off('click', closeOnMapClick);
    };
  }, [clearSelection, map]);

  return (
    <>
      <Source id={sourceId} minzoom={trailheadsMinZoom} tiles={tiles} type="vector">
        <TrailheadVectorPinLayer
          collapsedActiveLayerId={trailheadsOverlayCollapsedActiveId}
          collapsedLayerId={trailheadsOverlayCollapsedId}
          filter={pinFilter}
          isClosedExpression={isClosedExpression}
          isCollapsedExpression={isCollapsedExpression}
          isCompletedExpression={isCompletedExpression}
          isHoveredExpression={isHoveredExpression}
          isSavedExpression={isSavedExpression}
          isSelectedExpression={isSelectedExpression}
          layerId={trailheadsOverlayLayerId}
          popularityExpression={popularityExpression}
          sourceId={sourceId}
          sourceLayer={sourceLayer}
          trailCountExpression={trailCountExpression}
        />
      </Source>
      {selectedTrailhead && selectedTrails.length ? (
        <TrailheadPopup
          activityType={activityType}
          cardLocation={cardLocation}
          detailedCardLocation={detailedCardLocation}
          getCTAProps={getCTAProps}
          getTrailCardUrl={getTrailCardUrl}
          inspirationalPageSlug={inspirationalPageSlug}
          isComplete={isTrailCompleted}
          isFavorite={isTrailSaved}
          isVerified={isTrailVerified}
          onFavoriteClick={handleTrailFavoriteClick}
          onTrailCardViewed={onTrailCardViewed}
          onTrailChange={trail => {
            popupTrailsRef.current = trail ? { id: selectedTrailhead.id, trails: [trail] } : undefined;
            dispatch(updateActiveTrails(trail ? [trail] : undefined));
          }}
          onTrailNavigate={onTrailNavigate}
          trailhead={selectedTrailhead}
          trails={selectedTrails}
        />
      ) : null}
      <ActiveTrailPolylines maxPolylines={maxPolylines} />
      {listModal}
      {signUpModal}
    </>
  );
}
